import { EcoaMediaItems } from "@components/common/EcoaMediaItems";
import { EcoaParagraph } from "@components/common/EcoaParagraph";
import styled from "@emotion/styled";
import { useLangContext } from "@layouts/home/LangContext";
import { CrfDescriptionItemSpec, CrfItemAlignment, CrfItemUiImgAlignment } from "maven-lib/dist/consts/edc/crf";
import { ECoaItemDescriptionContent, getCrfLabel } from "maven-lib/dist/consts/edc/lib";
import { CustomizedStyleLabelComponent } from "../VisitQuestionnaireWrapper";

interface StyledDescriptionParagraphWrapperType {
  align: CrfItemAlignment;
}

const StyledDescriptionParagraphWrapper = styled.div<StyledDescriptionParagraphWrapperType>`
  word-wrap: break-word;
  text-align: ${(props) => props.align.toLowerCase()};
`;

export type StyledDescriptionImageProps = {
  position?: CrfItemUiImgAlignment;
};

const StyledDescriptionImage = styled.img<StyledDescriptionImageProps>`
  cursor: pointer;
  max-width: ${({ position }) => (position === "RIGHT" ? "70%" : "100%")};
  max-height: 450px;
  object-fit: contain;
`;

export type StyledDescriptionVideoProps = {
  position?: CrfItemUiImgAlignment;
};

const StyledDescriptionWrapper = styled.div<StyledDescriptionImageProps>`
  ${({ position }) => {
    if (!position) return "";

    return `
    display: flex; 
    gap: 15px;
    flex-direction: ${position === "TOP" ? "column-reverse" : position === "BOTTOM" ? "column" : "row"}; 
  `;
  }}
`;

interface StyledLabelComponentProps {
  labelStyle?: ECoaItemDescriptionContent;
}

const DescriptionStyledLabelComponent = ({ labelStyle }: StyledLabelComponentProps) => {
  if (typeof labelStyle === "string") {
    return <CustomizedStyleLabelComponent>{labelStyle}</CustomizedStyleLabelComponent>;
  } else {
    return (
      <CustomizedStyleLabelComponent
        bold={labelStyle?.ecoa?.TextStyle?.bold}
        italic={labelStyle?.ecoa?.TextStyle?.italic}
        lineBreak={labelStyle?.ecoa?.TextStyle?.lineBreak}
        color={labelStyle?.ecoa?.TextStyle?.color?.value}
        underline={labelStyle?.ecoa?.TextStyle?.underline}
      >
        {getCrfLabel(labelStyle?.text)}
      </CustomizedStyleLabelComponent>
    );
  }
};

interface QuestionnaireDescriptionItemProps {
  item: CrfDescriptionItemSpec;
}

export function QuestionnaireDescriptionItem(props: QuestionnaireDescriptionItemProps) {
  const { fontSize } = useLangContext();
  const { item } = props;
  const { description, align = "LEFT" } = item;
  const uiImg = !!item.uiImg && "url" in item.uiImg ? item.uiImg : undefined;
  const uiMediaItems = item.uiMedia?.media || [];
  const uiMediaDisplay = item.uiMedia?.display;
  const questionSize = fontSize === "LARGE" ? "8X-LARGE" : fontSize === "MEDIUM" ? "5X-LARGE" : "3X-LARGE";

  const convertToParagraph = (description: ECoaItemDescriptionContent[]): ECoaItemDescriptionContent[][] => {
    return description.reduce<ECoaItemDescriptionContent[][]>((acc, cur: ECoaItemDescriptionContent) => {
      const lastLength = acc.length === 0 ? 0 : acc.length - 1;

      if (typeof cur === "object" && cur.ecoa?.TextStyle?.lineBreak) {
        if (acc[lastLength] !== undefined) {
          if (acc[lastLength].length > 0 && cur.ecoa?.TextStyle?.lineBreak.before) acc.push([cur]);
          else acc[lastLength].push(cur);
        } else acc.push([cur]);

        if (cur.ecoa?.TextStyle?.lineBreak.after) acc.push([]);
      } else {
        if (acc[lastLength] !== undefined) acc[lastLength].push(cur);
        else acc.push([cur]);
      }

      return acc;
    }, []);
  };

  return (
    <StyledDescriptionWrapper position={uiImg?.position || uiMediaDisplay?.position}>
      <StyledDescriptionParagraphWrapper align={align}>
        {typeof description === "string" ? (
          <EcoaParagraph text={description} size={questionSize} />
        ) : Array.isArray(description) ? (
          <>
            {convertToParagraph(description).map((desc, i) => {
              const paragraph = desc.find((p) => typeof p === "object" && p.ecoa?.paragraphAlign);
              const paragraphAlign = typeof paragraph === "object" ? paragraph.ecoa?.paragraphAlign : undefined;

              return (
                <EcoaParagraph key={i} align={paragraphAlign} size={questionSize}>
                  {desc.map((labelStyle, index) => {
                    if (typeof labelStyle === "string") {
                      return <CustomizedStyleLabelComponent key={index}>{labelStyle}</CustomizedStyleLabelComponent>;
                    }
                    return <DescriptionStyledLabelComponent key={index} labelStyle={labelStyle} />;
                  })}
                </EcoaParagraph>
              );
            })}
          </>
        ) : (
          <DescriptionStyledLabelComponent labelStyle={description} />
        )}
      </StyledDescriptionParagraphWrapper>
      {uiImg?.url && (
        <StyledDescriptionImage
          src={uiImg.url}
          alt="description"
          position={uiImg?.position}
          onClick={() => {
            const width = Math.min(window.innerWidth * 0.7, 1024);
            const height = (width * 9) / 16;

            window.open(uiImg.url, undefined, `width=${width}},height=${height}`);
          }}
        />
      )}
      <EcoaMediaItems media={uiMediaItems} placement={uiMediaDisplay?.placement} alignment={uiMediaDisplay?.alignment} />
    </StyledDescriptionWrapper>
  );
}
